// BreadcrumbSegment — Option C (hover-reveal chevron) primitive.
//
// At rest: plain text (like a text link). On hover: a tiny ▾ chevron slides in.
//   - Click TEXT  → onNavigate() (jumps to that level's home, truncating chain).
//                   If no onNavigate is set, text click opens the switcher
//                   (workspace/project case — those have no dedicated URL).
//   - Click ▾     → opens the switcher dropdown for that level.
//
// Variants:
//   - default      : standard breadcrumb segment.
//   - placeholder  : muted/italic "+ {LevelName}" affordance for the next level
//                    the user hasn't picked yet (App / Artifact / Sub-artifact).
//
// `isCurrent` bolds the segment and routes text click → switcher (you're here,
// nothing to navigate to). The chevron still reveals on hover.
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { clsx } from "clsx";

export interface BreadcrumbOption {
  id: string;
  label: string;
  icon?: string;
  iconNode?: ReactNode;
  envTone?: "production" | "staging" | "development" | "neutral";
  hint?: string;
}

/** A titled group inside the switcher menu (48.23 plural slot): each section carries its own
 *  options, select handler and create row — e.g. the workspace-level "+ Add" slot offers
 *  CLIENTS and ENGAGEMENTS (internal/host) side by side, honest to the non-sequential model. */
export interface BreadcrumbSection {
  key: string;
  title: string;
  options: BreadcrumbOption[];
  onSelect: (id: string) => void;
  createLabel?: string;
  onCreate?: (prefill?: string) => void;
  createDisabled?: boolean;
  createDisabledReason?: string;
  onRenameItem?: (id: string, name: string) => void;
  onDeleteItem?: (id: string) => void;
}

export interface BreadcrumbSegmentProps {
  icon?: ReactNode;
  label: string;
  /** Level type shown as a tiny uppercase eyebrow above the name (Workspace/Client/…). */
  typeLabel?: string;
  envTone?: BreadcrumbOption["envTone"];
  isCurrent?: boolean;
  onNavigate?: () => void;
  options?: BreadcrumbOption[];
  activeId?: string;
  onSelect?: (id: string) => void;
  menuTitle?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  createLabel?: string;
  /** Called when the create row is clicked. Receives the current search text (trimmed) when
   *  non-empty, so a no-match search can create the typed entity directly (prefilled). */
  onCreate?: (prefill?: string) => void;
  /** Disable the create row (e.g. parent not created yet — creating would fail with no parent
   *  to link). Renders greyed + non-clickable with `createDisabledReason` as the tooltip. */
  createDisabled?: boolean;
  createDisabledReason?: string;
  /** When set, each option row gets hover Rename (inline edit) / Delete (inline confirm) actions. */
  onRenameItem?: (id: string, name: string) => void;
  onDeleteItem?: (id: string) => void;
  /** Sectioned menu (48.23): when set, the menu renders these titled groups (each with its own
   *  options/select/create) INSTEAD of the flat `options` list. Search filters every section. */
  sections?: BreadcrumbSection[];
  variant?: "default" | "placeholder";
}

export interface BreadcrumbSegmentHandle {
  openMenu: () => void;
}

const envDot: Record<NonNullable<BreadcrumbOption["envTone"]>, string> = {
  production: "bg-[var(--color-success-500)]",
  staging: "bg-[var(--color-warning-500)]",
  development: "bg-[var(--color-accent)]",
  neutral: "bg-[var(--color-neutral-400)]",
};

interface MenuPos { top: number; left: number; }

const MENU_MIN_W = 256;
const MENU_GAP = 8; // match the topbar menus (Help/Account) vertical offset — appshell-wide
const VIEWPORT_PAD = 8;

export const BreadcrumbSegment = forwardRef<BreadcrumbSegmentHandle, BreadcrumbSegmentProps>(function BreadcrumbSegment(
  {
    icon,
    label,
    typeLabel,
    envTone,
    isCurrent,
    onNavigate,
    options = [],
    activeId,
    onSelect,
    menuTitle,
    searchable,
    searchPlaceholder,
    createLabel,
    onCreate,
    createDisabled,
    createDisabledReason,
    onRenameItem,
    onDeleteItem,
    sections,
    variant = "default",
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const sectionOptionCount = sections?.reduce((n, s) => n + s.options.length, 0) ?? 0;
  const showSearch = searchable || options.length >= 5 || sectionOptionCount >= 5;
  const hasMenu =
    options.length > 0 || Boolean(onCreate) || (sections?.some((s) => s.options.length > 0 || s.onCreate) ?? false);
  const isPlaceholder = variant === "placeholder";

  const computePos = (): MenuPos | null => {
    if (!wrapRef.current) return null;
    const rect = wrapRef.current.getBoundingClientRect();
    let left = rect.left;
    if (left + MENU_MIN_W > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, window.innerWidth - MENU_MIN_W - VIEWPORT_PAD);
    }
    // Open below the containing top bar (header), like the topbar icon popovers — the
    // segment is vertically centered/shorter than the bar, so anchoring to the segment
    // would open inside/overlapping it. Fall back to the segment when not in a header.
    const headerBottom = wrapRef.current.closest("header")?.getBoundingClientRect().bottom;
    const top = headerBottom != null ? headerBottom : rect.bottom + MENU_GAP;
    return { top, left };
  };

  const openMenu = () => {
    if (!hasMenu) return;
    setPos(computePos());
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setPos(null);
  };

  useImperativeHandle(ref, () => ({ openMenu }), [hasMenu]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        closeMenu();
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeMenu(); };
    const onReposition = () => setPos(computePos());
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setEditingId(null);
      setConfirmDeleteId(null);
    } else if (showSearch) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, showSearch]);

  const filterOpts = (opts: BreadcrumbOption[]): BreadcrumbOption[] => {
    if (!query.trim()) return opts;
    const q = query.toLowerCase();
    return opts.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint?.toLowerCase().includes(q) ?? false),
    );
  };
  const filtered = useMemo(() => filterOpts(options), [options, query]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shared row renderers — the flat and sectioned (48.23) menu paths render IDENTICAL rows,
  //    differing only in grouping + which handlers apply. ──
  const renderOptionRow = (
    o: BreadcrumbOption,
    h: {
      onSelect?: (id: string) => void;
      onRenameItem?: (id: string, name: string) => void;
      onDeleteItem?: (id: string) => void;
    },
  ) => {
    const active = o.id === activeId;
    const hasActions = Boolean(h.onRenameItem || h.onDeleteItem);
    if (editingId === o.id) {
      const commit = (value: string) => {
        const v = value.trim();
        if (v && v !== o.label) h.onRenameItem?.(o.id, v);
        setEditingId(null);
      };
      return (
        <div key={o.id} className="px-3 py-1.5">
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(e.currentTarget.value); else if (e.key === "Escape") setEditingId(null); }}
            onBlur={(e) => commit(e.currentTarget.value)}
            aria-label={`Rename ${o.label}`}
            className="w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
          />
        </div>
      );
    }
    return (
      <div
        key={o.id}
        role="option"
        aria-selected={active}
        className="group/row w-full flex items-center gap-1 pr-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
      >
        {/* No selected bg — the ✓ marks the current item (matches the switcher). */}
        <button
          type="button"
          onClick={() => { h.onSelect?.(o.id); closeMenu(); }}
          className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-left"
        >
          {o.iconNode ?? (o.icon && <span aria-hidden>{o.icon}</span>)}
          {o.envTone && <span className={clsx("w-1.5 h-1.5 rounded-full", envDot[o.envTone])} aria-hidden />}
          <span className="flex-1 truncate">{o.label}</span>
          {o.hint && <span className="text-xs text-[var(--color-text-secondary)]">{o.hint}</span>}
          {active && <Check size={14} className="text-[var(--color-accent)]" aria-hidden />}
        </button>
        {hasActions && confirmDeleteId === o.id ? (
          <span className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-[var(--color-text-secondary)]">Delete?</span>
            <button type="button" title="Confirm delete" aria-label={`Confirm delete ${o.label}`}
              onClick={() => { h.onDeleteItem?.(o.id); setConfirmDeleteId(null); }}
              className="p-1 rounded-[2px] text-[var(--color-danger-500)] hover:bg-[var(--color-surface-base)]">
              <Check size={14} aria-hidden />
            </button>
            <button type="button" title="Cancel" aria-label="Cancel delete"
              onClick={() => setConfirmDeleteId(null)}
              className="p-1 rounded-[2px] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-base)]">
              <X size={14} aria-hidden />
            </button>
          </span>
        ) : hasActions ? (
          <span className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100">
            {h.onRenameItem && (
              <button type="button" title="Rename" aria-label={`Rename ${o.label}`}
                onClick={() => { setEditValue(o.label); setEditingId(o.id); }}
                className="p-1 rounded-[2px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-base)]">
                <Pencil size={13} aria-hidden />
              </button>
            )}
            {h.onDeleteItem && (
              <button type="button" title="Delete" aria-label={`Delete ${o.label}`}
                onClick={() => setConfirmDeleteId(o.id)}
                className="p-1 rounded-[2px] text-[var(--color-text-secondary)] hover:text-[var(--color-danger-500)] hover:bg-[var(--color-surface-base)]">
                <Trash2 size={13} aria-hidden />
              </button>
            )}
          </span>
        ) : null}
      </div>
    );
  };

  const renderCreateRow = (
    rowLabel: string,
    create: (prefill?: string) => void,
    disabled?: boolean,
    disabledReason?: string,
  ) => (
    <>
      <button
        type="button"
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => {
          if (disabled) return;
          create(query.trim() || undefined);
          closeMenu();
        }}
        className={clsx(
          "w-full flex items-center gap-2 px-3 py-2 text-sm text-left",
          disabled
            ? "text-[var(--color-text-disabled)] cursor-not-allowed"
            : "text-[var(--color-accent)] hover:bg-[var(--color-surface-muted)]",
        )}
      >
        <Plus size={14} aria-hidden />
        <span className="flex-1 truncate">
          {query.trim() ? `Create “${query.trim()}”` : rowLabel}
        </span>
      </button>
      {disabled && disabledReason && (
        <p className="px-3 pb-2 -mt-1 text-xs text-[var(--color-text-secondary)]">{disabledReason}</p>
      )}
    </>
  );

  // Text click: navigate when available; otherwise open the switcher.
  // "Current" segments always open the switcher on text click (you're already here).
  const handleTextClick = () => {
    if (isCurrent && hasMenu) {
      openMenu();
      return;
    }
    if (onNavigate) onNavigate();
    else if (hasMenu) openMenu();
  };

  const handleChevronClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (open) closeMenu(); else openMenu();
  };

  const textClass = clsx(
    "inline-flex items-center gap-1 text-sm leading-normal truncate max-w-48",
    isPlaceholder
      ? "italic text-[var(--color-text-disabled)] hover:text-[var(--color-text-secondary)]"
      : isCurrent
        ? "font-semibold text-[var(--color-text-primary)]"
        : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:underline",
  );

  const titleText = isPlaceholder
    ? `Add ${label}`
    : isCurrent
      ? `Switch ${label}`
      : onNavigate
        ? `Go to ${label}`
        : `Switch ${label}`;

  return (
    <span ref={wrapRef} className="group relative inline-flex items-center gap-0.5 shrink-0">
      <button
        type="button"
        onClick={handleTextClick}
        title={titleText}
        className={clsx(
          textClass,
          "rounded-sm px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
        )}
      >
        {isPlaceholder ? (
          <>
            <Plus size={12} className="shrink-0" aria-hidden />
            <span className="truncate">{label}</span>
          </>
        ) : typeLabel && isCurrent ? (
          <span className="flex flex-col items-start gap-0.5 leading-none">
            <span
              className={clsx(
                "text-[9px] font-semibold uppercase tracking-wide leading-none",
                isCurrent ? "text-[var(--color-accent)]" : "text-[var(--color-text-secondary)]",
              )}
            >
              {typeLabel}
            </span>
            <span className="inline-flex items-center gap-1">
              {icon && <span className="text-[var(--color-text-secondary)] shrink-0">{icon}</span>}
              {envTone && <span className={clsx("w-1.5 h-1.5 rounded-full shrink-0", envDot[envTone])} aria-hidden />}
              <span className="truncate">{label}</span>
            </span>
          </span>
        ) : (
          <>
            {icon && <span className="text-[var(--color-text-secondary)] shrink-0">{icon}</span>}
            {envTone && <span className={clsx("w-1.5 h-1.5 rounded-full shrink-0", envDot[envTone])} aria-hidden />}
            <span className="truncate">{label}</span>
          </>
        )}
      </button>

      {hasMenu && (
        <button
          type="button"
          onClick={handleChevronClick}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={menuTitle ?? `Switch ${label}`}
          title={menuTitle ?? `Switch ${label}`}
          className={clsx(
            "inline-flex items-center justify-center w-4 h-4 rounded-sm shrink-0",
            "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
            "transition-opacity",
            open ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
          )}
        >
          <ChevronDown size={12} aria-hidden />
        </button>
      )}

      {open && pos && hasMenu &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{ position: "fixed", top: pos.top, left: pos.left }}
            className="z-50 min-w-64 bg-[var(--surface-sunken)] border border-[var(--color-border-default)] rounded-[2px] shadow-[var(--shadow-lg)] py-1"
          >
            {menuTitle && (
              <div className="px-3 py-1.5 text-xs uppercase tracking-wide text-[var(--color-text-secondary)] border-b border-[var(--color-border-subtle)]">
                {menuTitle}
              </div>
            )}

            {showSearch && (
              <div className="px-2 py-1.5 border-b border-[var(--color-border-subtle)]">
                <div className="flex items-center gap-1.5 h-8 px-2 rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)]">
                  <Search size={14} className="text-[var(--color-text-secondary)] shrink-0" aria-hidden />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder ?? "Search…"}
                    aria-label="Filter options"
                    className="flex-1 min-w-0 bg-transparent text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] focus:outline-none"
                  />
                </div>
              </div>
            )}

            {sections ? (
              // Sectioned menu (48.23 plural slot): titled groups, each with its own rows + create.
              <div className="max-h-72 overflow-y-auto">
                {sections.map((sec) => {
                  const secFiltered = filterOpts(sec.options);
                  return (
                    <div key={sec.key} className="border-b border-[var(--color-border-subtle)] last:border-b-0">
                      <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                        {sec.title}
                      </div>
                      {secFiltered.map((o) => renderOptionRow(o, sec))}
                      {secFiltered.length === 0 && query.trim() !== "" && (
                        <div className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">No matches.</div>
                      )}
                      {sec.createLabel && sec.onCreate &&
                        renderCreateRow(sec.createLabel, sec.onCreate, sec.createDisabled, sec.createDisabledReason)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="max-h-72 overflow-y-auto">
                  {filtered.map((o) => renderOptionRow(o, { onSelect, onRenameItem, onDeleteItem }))}
                  {filtered.length === 0 && (
                    <div className="px-3 py-3 text-sm text-[var(--color-text-secondary)] text-center">
                      {query ? `No matches for "${query}".` : "No options."}
                    </div>
                  )}
                </div>

                {createLabel && onCreate && (
                  <div className="border-t border-[var(--color-border-subtle)]">
                    {renderCreateRow(createLabel, onCreate, createDisabled, createDisabledReason)}
                  </div>
                )}
              </>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
});
